import { lazy, Suspense, forwardRef } from 'react'

/**
 * Replacement for loadable() to mimic API but use React.lazy + Suspense internally.
 * Now properly forwards refs to the loaded component.
 *
 * @param {Function} importFn - The dynamic import function.
 * @param {Object} options - Options object, supports { fallback }.
 * @returns {React.FC} - Wrapped component ready to use directly with ref support.
 */
export default function loadable(importFn, options = {}) {
	const { fallback } = options

	// Assign displayName for easier debugging
	const importMatch = importFn.toString().match(/['"](.+?)['"]/)
	const inferredName = importMatch ? importMatch[1].split('/').pop() : 'LoadableComponent'

	const LazyComponent = lazy(() => loadWithRetry(importFn, { moduleName: inferredName }))

	// Create the wrapper component with forwardRef to properly handle refs
	const Wrapped = forwardRef((props, ref) => {
		const UI = <LazyComponent {...props} ref={ref} />
		if (fallback) return <Suspense fallback={fallback}>{UI}</Suspense>
		return UI
	})

	Wrapped.displayName = `Loadable(${inferredName})`

	return Wrapped
}

function loadWithRetry(importFunc, { maxRetries = 3, retryDelay = 500, moduleName } = {}) {
	return new Promise((resolve, reject) => {
		const attempt = (n) => {
			importFunc()
				.then(resolve)
				.catch((error) => {
					if (n === 0) {
						console.error(`Module "${moduleName}" import failed after retries:`, error)
						reject(error)
						return
					}
					setTimeout(() => attempt(n - 1), retryDelay)
				})
		}
		attempt(maxRetries)
	})
}
